/**
 * We may eventually need to split the Jira presets out to the various
 * - JSW
 * - JSM
 * - JWM
 * - JPD
 *
 * Variations, however we'll start simple for now.
 */
/**
 * Please reference
 * https://hello.atlassian.net/wiki/spaces/CONF/pages/2714833063/Gen+AI+in+Editor+EAP2+-+Product-specific+requirements
 * Also see: https://hello.atlassian.net/wiki/spaces/EDAI/pages/2731933104/Editor+AI+EAP+Part+2+Dev+Documentation+Prebuilt+Providers+Plugin+Presets
 */

import type { EditorPluginAIProvider, EditorPluginAIPublicOptions } from '../../types/types';

import { isEditorPluginAIConfigItemWithOptions } from './config-items/config-items';
import { brainstormWithOptions } from './config-items/prompts/brainstorm/brainstorm';
import {
	changeToneToCasualWithOptions,
	changeToneToEducationalWithOptions,
	changeToneToEmpatheticWithOptions,
	changeToneToNeutralWithOptions,
	changeToneToProfessionalWithOptions,
} from './config-items/prompts/change-tone/change-tone';
import { createDraftReplyWithOptions } from './config-items/prompts/draft-reply/draft-reply';
import {
	fixSpellingAndGrammarWithOptions,
	improveWritingWithOptions,
} from './config-items/prompts/enhance/enhance';
import { freeGenerate } from './config-items/prompts/free-generate/free-generate';
import { createImproveDescriptionWithOptions } from './config-items/prompts/improve-description/improve-description';
import { makeShorterWithOptions } from './config-items/prompts/make-shorter/make-shorter';
import { summarizeWritingWithOptions } from './config-items/prompts/summarize-writing/summarize-writing';
import { translationConfigItemWithOptions } from './config-items/prompts/translate/translate';
import { _getFetchCustomHeaders } from './utils';

type JiraEditorPluginAIOptions = EditorPluginAIPublicOptions & {
	allowIssueReformatter?: boolean;
	allowSuggestResponse?: boolean;
	issueId?: string;
};

export const createJiraEditorPluginAIOptions = (
	options: JiraEditorPluginAIOptions,
): EditorPluginAIProvider => {
	// Now deprecated - migrate to handleFeedbackSubmission
	const onExperienceEvent = options?.onExperienceEvent || {
		THUMBS_UP: () => {},
		THUMBS_DOWN: () => {},
		REVIEW_STATE_ENTERED: () => {},
		EXPERIENCE_COMPLETE: () => {},
	};

	// Suggest response is only enabled for JSM
	const isSuggestResponseEnabled =
		options.allowSuggestResponse && options.issueId !== undefined && options.product === 'JSM';

	return {
		aiGlobalOptIn: options.aiGlobalOptIn,
		baseGenerate: freeGenerate,
		configItemWithOptions: [
			!!options.allowIssueReformatter &&
				createImproveDescriptionWithOptions({
					getAdditionalProps: options.getAdditionalProps || (() => ({})),
				}),
			!!isSuggestResponseEnabled && createDraftReplyWithOptions(options.issueId),
			summarizeWritingWithOptions,
			improveWritingWithOptions,
			fixSpellingAndGrammarWithOptions,
			!isSuggestResponseEnabled && brainstormWithOptions,
			makeShorterWithOptions,
			changeToneToProfessionalWithOptions,
			changeToneToEmpatheticWithOptions,
			changeToneToCasualWithOptions,
			changeToneToNeutralWithOptions,
			changeToneToEducationalWithOptions,
			...translationConfigItemWithOptions,
		].filter(isEditorPluginAIConfigItemWithOptions),
		disableAISelectionToolbar: true,
		generativeAIApiUrl: options.generativeAIApiUrl,
		product: options.product,
		objectId: options.issueId,
		getFetchCustomHeaders: options?.getFetchCustomHeaders || _getFetchCustomHeaders,
		onExperienceEvent,
		handleFeedbackSubmission: options?.handleFeedbackSubmission,
		AIButtonWrapper: options.AIButtonWrapper,
		PromptEditor: options.PromptEditor,
		disableInterrogation: options.disableInterrogation ?? false,
		getChannelVariables: options.getChannelVariables,
		actionSideEffects: options.actionSideEffects,
		actionOverrides: options.actionOverrides,
		isRovoEnabled: options.isRovoEnabled ?? true,
		aiLifeCycleDynamicAttributesGetter: options.aiLifeCycleDynamicAttributesGetter,
		auditLogSettings: options.auditLogSettings,
		getAdditionalProps: options.getAdditionalProps,
	};
};
